import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import React, { memo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import FilesUploader from "../../components/FilesUploader";
import ServiceCard from "../../components/ServiceCard";
import ButtonControl from "../../components/controls/ButtonControl";
import InputControl from "../../components/controls/InputControl";
import RadioGroupControl from "../../components/controls/RadioGroupControl";
import SelectControl from "../../components/controls/SelectControl";
import { API_QUOTES } from "../../context/ApiEndPoints";
import useAppContext from "../../hooks/useAppContext";
import FormCustomerInfo from "./FormCustomerInfo";

const objRecord = {
  industry: "Architectural",
  necessaryInfo_: "",
  email: "",
  firstName: "",
  lastName: "",
  address: "",
  city: "",
  state: "",
  country: "",
  zipCode: "",
  phoneNumber: "",
  company: "",
  taxNumber: "",
  whereFindUs: "",
};

function FormArchitectural({ quoteId }) {
  const { user } = useAppContext();
  const navigate = useNavigate();

  const [values, setValues] = useState({ ...objRecord });
  const [errors, setErrors] = useState({});
  const [responseId, setResponseId] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const validate = (input = values) => {
    let temp = { ...errors };

    // Customer Info validation
    if ("email" in input) {
      temp.email = /^[A-Z0-9._%+-]+@([A-Z0-9-]+\.)+[A-Z]{2,4}$/i.test(
        input.email
      )
        ? ""
        : "Email in not valid.";
      temp.email = input.email ? temp.email : "Email is required.";
    }
    if ("firstName" in input)
      temp.firstName = input.firstName ? "" : "This field is required.";
    if ("lastName" in input)
      temp.lastName = input.lastName ? "" : "This field is required.";
    if ("city" in input)
      temp.city = input.city ? "" : "This field is required.";
    if ("state" in input)
      temp.state = input.state ? "" : "This field is required.";
    if ("country" in input)
      temp.country = input.country ? "" : "This field is required.";
    if ("zipCode" in input)
      temp.zipCode = input.zipCode ? "" : "This field is required.";
    if ("phoneNumber" in input)
      temp.phoneNumber = input.phoneNumber ? "" : "This field is required.";

    setErrors({ ...temp });
    if (input === values) return Object.values(temp).every((x) => x === "");
  };

  const handleInputChange = (e, switchName = null) => {
    const { name, value } = e.target;
    values[name] = value;
    if (switchName) values[switchName] = true;

    setValues({ ...values });
    validate({ [name]: value });
  };

  const [step, setStep] = useState(0);
  const [items, setItems] = useState([]);
  const nextStep = () => {
    // ARRAY: create selected services array;
    const x = Object.keys(values).filter((k) => k.startsWith("_") && values[k]);
    setItems(x);
    setStep(step + 1);
  };

  // Go back to prev step
  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validate()) {
      setIsSaving(true);

      const data = {};
      const quoteServices = {};
      const quoteItems = {};
      const quoteInfo = {};
      for (const [key, val] of Object.entries(values)) {
        if (key.startsWith("_") && val) {
          const k = key.slice(1);
          quoteServices[k] = val;
          continue;
        }

        if (key.endsWith("_") && val) {
          const a = key.slice(0, -1);
          const b = a.replace(/([A-Z])/g, " $1").trim();
          const k = b.replace(/^\w/, (c) => c.toUpperCase());
          quoteInfo[k] = val;
          continue;
        }

        if (/^[A-Z]/.test(key[0]) && val) {
          quoteItems[key] = typeof val === "boolean" ? "Yes" : val;
          continue;
        }

        if (/^[a-z]/.test(key[0])) {
          data[key] = val;
          continue;
        }
      }

      for (const [key] of Object.entries(data)) {
        if (key.endsWith("_") || key.startsWith("_")) {
          delete data[key];
        }
      }

      let record = { ...data };
      record.quoteServices = JSON.stringify(quoteServices);
      record.quoteItems = JSON.stringify(quoteItems);
      record.quoteInfo = JSON.stringify(quoteInfo);

      // Create formData
      const formData = new FormData();
      // Add quote values to formData
      for (const [key, val] of Object.entries(record)) {
        formData.append(key, val);
      }

      // Add buildingWrapFile values to formData
      if (values.buildingWrapFile) {
        for (const val of Object.values(values.buildingWrapFile)) {
          formData.append("buildingWrapFile", val);
        }
      }

      // Add canvasFramesFile values to formData
      if (values.canvasFramesFile) {
        for (const val of Object.values(values.canvasFramesFile)) {
          formData.append("canvasFramesFile", val);
        }
      }

      // Add furnitureFile values to formData
      if (values.furnitureFile) {
        for (const val of Object.values(values.furnitureFile)) {
          formData.append("furnitureFile", val);
        }
      }

      // Add wallWrapFile values to formData
      if (values.wallWrapFile) {
        for (const val of Object.values(values.wallWrapFile)) {
          formData.append("wallWrapFile", val);
        }
      }

      // Add windowFilmFile values to formData
      if (values.windowFilmFile) {
        for (const val of Object.values(values.windowFilmFile)) {
          formData.append("windowFilmFile", val);
        }
      }

      // Add infoFiles values to formData
      if (values.infoFile) {
        for (const val of Object.values(values.infoFile)) {
          formData.append("infoFile", val);
        }
      }

      // POST request using fetch
      const requestOptions = {
        method: "POST",
        headers: { Authorization: "Bearer " + user.token },
        body: formData,
      };

      try {
        const response = await fetch(API_QUOTES, requestOptions);
        const isJson = response.headers
          .get("content-type")
          ?.includes("application/json");
        const json = isJson && (await response.json());

        // check for error response
        if (!response.ok) {
          // get error message from body or default to response status
          const error = (json && json.message) || response.status;
          throw Error(error);
        }

        setStep(step + 1);
        setValues({ ...objRecord });
        setErrors({});
        setResponseId(json);
      } catch (error) {
        setFetchError(
          <Alert severity="error">
            {!error.message ? error : error.message}
          </Alert>
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Slide
          direction="right"
          in={step === 0 ? true : false}
          style={{ display: step === 0 ? "flex" : "none" }}
        >
          <Grid container>
            <Grid item xs={12} md={9}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "BuildingWrapOption",
                      "buildingWrapFile",
                      "BuildingWrapQuantity",
                      "BuildingWrapHeight",
                      "BuildingWrapWidth",
                      "BuildingWrapUnits",
                      "BuildingWrapMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_BuildingWrap"
                    icon="wi-building-wrap"
                    title="Building Wrap"
                  >
                    <RadioGroupControl
                      aria-label="building wrap"
                      name="BuildingWrapOption"
                      onChange={(e) => handleInputChange(e, "_BuildingWrap")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="buildingWrapFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="BuildingWrapQuantity"
                      onChange={(e) => handleInputChange(e, "_BuildingWrap")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="BuildingWrapHeight"
                        onChange={(e) => handleInputChange(e, "_BuildingWrap")}
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="BuildingWrapWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_BuildingWrap");
                          if (!values.BuildingWrapWidth)
                            setValues({ ...values, BuildingWrapUnits: "" });
                        }}
                      />
                      {values.BuildingWrapWidth && (
                        <SelectControl
                          label="Units"
                          name="BuildingWrapUnits"
                          value={
                            values.BuildingWrapUnits
                              ? values.BuildingWrapUnits
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "_BuildingWrap")
                          }
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell Me More"
                      name="BuildingWrapMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_BuildingWrap")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "CanvasFramesOption",
                      "canvasFramesFile",
                      "CanvasFramesQuantity",
                      "CanvasFramesHeight",
                      "CanvasFramesWidth",
                      "CanvasFramesUnits",
                      "CanvasFramesMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_CanvasFrames"
                    icon="wi-canvas-frames"
                    title="Canvas &amp; Frames"
                  >
                    <RadioGroupControl
                      aria-label="canvas frames"
                      name="CanvasFramesOption"
                      onChange={(e) => handleInputChange(e, "_CanvasFrames")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="canvasFramesFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="CanvasFramesQuantity"
                      onChange={(e) => handleInputChange(e, "_CanvasFrames")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="CanvasFramesHeight"
                        onChange={(e) => handleInputChange(e, "_CanvasFrames")}
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="CanvasFramesWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_CanvasFrames");
                          if (!values.CanvasFramesWidth)
                            setValues({ ...values, CanvasFramesUnits: "" });
                        }}
                      />
                      {values.CanvasFramesWidth && (
                        <SelectControl
                          label="Units"
                          name="CanvasFramesUnits"
                          value={
                            values.CanvasFramesUnits
                              ? values.CanvasFramesUnits
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "_CanvasFrames")
                          }
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell Me More"
                      name="CanvasFramesMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_CanvasFrames")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "FurnitureOption",
                      "furnitureFile",
                      "FurnitureQuantity",
                      "FurnitureHeight",
                      "FurnitureWidth",
                      "FurnitureUnits",
                      "FurnitureMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_Furniture"
                    icon="wi-furniture"
                    title="Furniture"
                  >
                    <RadioGroupControl
                      aria-label="furniture"
                      name="FurnitureOption"
                      onChange={(e) => handleInputChange(e, "_Furniture")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="furnitureFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="FurnitureQuantity"
                      onChange={(e) => handleInputChange(e, "_Furniture")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="FurnitureHeight"
                        onChange={(e) => handleInputChange(e, "_Furniture")}
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="FurnitureWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_Furniture");
                          if (!values.FurnitureWidth)
                            setValues({ ...values, FurnitureUnits: "" });
                        }}
                      />
                      {values.FurnitureWidth && (
                        <SelectControl
                          label="Units"
                          name="FurnitureUnits"
                          value={
                            values.FurnitureUnits ? values.FurnitureUnits : ""
                          }
                          onChange={(e) => handleInputChange(e, "_Furniture")}
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell Me More"
                      name="FurnitureMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_Furniture")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "WallWrapOption",
                      "wallWrapFile",
                      "WallWrapQuantity",
                      "WallWrapHeight",
                      "WallWrapWidth",
                      "WallWrapUnits",
                      "WallWrapMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_WallWrap"
                    icon="wi-wall-wrap"
                    title="Wall Wrap"
                  >
                    <RadioGroupControl
                      aria-label="wall wrap"
                      name="WallWrapOption"
                      onChange={(e) => handleInputChange(e, "_WallWrap")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="wallWrapFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="WallWrapQuantity"
                      onChange={(e) => handleInputChange(e, "_WallWrap")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="WallWrapHeight"
                        onChange={(e) => handleInputChange(e, "_WallWrap")}
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="WallWrapWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_WallWrap");
                          if (!values.WallWrapWidth)
                            setValues({ ...values, WallWrapUnits: "" });
                        }}
                      />
                      {values.WallWrapWidth && (
                        <SelectControl
                          label="Units"
                          name="WallWrapUnits"
                          value={
                            values.WallWrapUnits ? values.WallWrapUnits : ""
                          }
                          onChange={(e) => handleInputChange(e, "_WallWrap")}
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell Me More"
                      name="WallWrapMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_WallWrap")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "WindowFilmOption",
                      "windowFilmFile",
                      "WindowFilmQuantity",
                      "WindowFilmHeight",
                      "WindowFilmWidth",
                      "WindowFilmUnits",
                      "WindowFilmMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_WindowFilm"
                    icon="wi-window-film"
                    title="Window Film"
                  >
                    <RadioGroupControl
                      aria-label="window film"
                      name="WindowFilmOption"
                      onChange={(e) => handleInputChange(e, "_WindowFilm")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="windowFilmFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="WindowFilmQuantity"
                      onChange={(e) => handleInputChange(e, "_WindowFilm")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="WindowFilmHeight"
                        onChange={(e) => handleInputChange(e, "_WindowFilm")}
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="WindowFilmWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_WindowFilm");
                          if (!values.WindowFilmWidth)
                            setValues({ ...values, WindowFilmUnits: "" });
                        }}
                      />
                      {values.WindowFilmWidth && (
                        <SelectControl
                          label="Units"
                          name="WindowFilmUnits"
                          value={
                            values.WindowFilmUnits ? values.WindowFilmUnits : ""
                          }
                          onChange={(e) => handleInputChange(e, "_WindowFilm")}
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell Me More"
                      name="WindowFilmMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_WindowFilm")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={["AddressDirections"]}
                    values={values}
                    setValues={setValues}
                    switchName="_OnSiteVisitRequest"
                    icon="wi-other"
                    title="On-Site Visit Request"
                  >
                    <InputControl
                      label="Address / Directions"
                      name="OnSiteAddressDirections"
                      multiline
                      rows={3}
                      onChange={(e) =>
                        handleInputChange(e, "_OnSiteVisitRequest")
                      }
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={["OtherDetails"]}
                    values={values}
                    setValues={setValues}
                    switchName="_Other"
                    icon="wi-other"
                    title="Other"
                  >
                    <InputControl
                      label="Please Specify"
                      name="OtherDetails"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_Other")}
                    />
                  </ServiceCard>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={3} textAlign="center">
              <Fab
                aria-label="go back"
                onClick={() => navigate(-1)}
                sx={{ mb: 3 }}
              >
                <ArrowBackIcon />
              </Fab>
              <Typography color="text.secondary">
                Select services form left and click CONFIRM once done
              </Typography>
              <Divider variant="middle" sx={{ my: 2 }} />
              {Object.keys(values).some(
                (k) => /^[A-Z]/.test(k[0]) && values[k]
              ) ? (
                <ButtonControl onClick={nextStep} text="CONFIRM" />
              ) : (
                <ButtonControl text="CONFIRM" disabled />
              )}
            </Grid>
          </Grid>
        </Slide>

        <Slide
          direction="left"
          in={step === 1 ? true : false}
          style={{ display: step === 1 ? "flex" : "none" }}
        >
          <Grid container>
            <Grid item xs={12} md={9}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <CardHeader
                      avatar={<InfoOutlinedIcon fontSize="large" />}
                      title="Other Info"
                    />
                    <Divider />
                    <CardContent>
                      <FormControl margin="normal" fullWidth>
                        <InputLabel>Upload Pictures</InputLabel>
                        <FilesUploader
                          inputName="infoFile"
                          accept="image/*"
                          values={values}
                          setValues={setValues}
                        />
                      </FormControl>
                      <InputControl
                        label="Any Other Necessary Info"
                        name="necessaryInfo_"
                        value={values.necessaryInfo_}
                        onChange={handleInputChange}
                        error={errors.necessaryInfo_}
                        multiline
                        rows={3}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormCustomerInfo
                    quoteId={quoteId}
                    values={values}
                    setValues={setValues}
                    handleInputChange={handleInputChange}
                    validate={validate}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={3} textAlign="center">
              <Fab onClick={prevStep} aria-label="go back" sx={{ mb: 3 }}>
                <ArrowBackIcon />
              </Fab>
              <Typography color="text.secondary">Selected services</Typography>
              <List>
                {items.map((item) => (
                  <ListItem key={item}>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item
                        .slice(1)
                        .replace(/([A-Z])/g, " $1")
                        .trim()}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider variant="middle" sx={{ my: 2 }}>
                {fetchError}
              </Divider>
              {!isSaving ? (
                <ButtonControl
                  type="submit"
                  text="Submit"
                  isSaving={isSaving}
                />
              ) : (
                <ButtonControl
                  text="...Processing"
                  isSaving={isSaving}
                  disabled
                />
              )}
            </Grid>
          </Grid>
        </Slide>
      </form>

      <Slide
        direction="left"
        in={step === 2 ? true : false}
        style={{ display: step === 2 ? "flex" : "none" }}
      >
        <Grid container>
          <Grid item xs={12} md={9}>
            <Alert severity="success">Quote Created Successfully!</Alert>
          </Grid>
          <Grid item xs={12} md={3} px={5} textAlign="center">
            {responseId && (
              <ButtonControl
                text="Done"
                color="success"
                startIcon={<CheckIcon />}
                component={Link}
                to={`/job-details/${responseId.quoteId}`}
              />
            )}
          </Grid>
        </Grid>
      </Slide>
    </>
  );
}

export default memo(FormArchitectural);
